import {
  Wrapper,
  SrOnly,
  Hero,
  HeartButton,
  GalleryButton,
  Content,
  BodyContainer,
  NarrowerContainer,
  Columns,
  MainColumn,
  SideColumn,
  AuthorColumn,
  AuthorRow,
  StoryAvatar,
  Title,
  InlineRow,
  AmenitiesGrid,
  IconLine,
  Divider,
  ActionButtons,
  ActionButton,
  BookingBox,
  BottomBar,
  BottomBarPrice,
  Block,
} from './styled'

export type DetailPageSkeletonVariant = 'story' | 'listing' | 'adventure'

type Props = {
  variant: DetailPageSkeletonVariant
}

const TitleLines = ({
  widths,
  height,
  ...rest
}: {
  widths: [string, string]
  height: string
  $mt?: string
  $mb?: string
}) => (
  <Title data-testid="detail-skeleton-title" {...rest}>
    <Block $w={widths[0]} $h={height} />
    <Block $w={widths[1]} $h={height} $mt="8px" />
  </Title>
)

const Paragraph = ({ lastWidth = '60%' }: { lastWidth?: string }) => (
  <>
    <Block $w="100%" $h="16px" $mt="22px" />
    <Block $w="100%" $h="16px" $mt="10px" />
    <Block $w={lastWidth} $h="16px" $mt="10px" />
  </>
)

const StoryContent = () => (
  <BodyContainer>
    <Content $pt="24px">
      <Columns>
        <AuthorColumn>
          <StoryAvatar data-testid="detail-skeleton-avatar" />
          <Block $w="120px" $h="14px" />
        </AuthorColumn>
        <MainColumn>
          <TitleLines
            widths={['90%', '52%']}
            height="26px"
            $mt="20px"
            $mb="8px"
          />
          <Block $w="60%" $h="15px" />
          <Paragraph lastWidth="72%" />
          {/* featured listing card */}
          <Block $w="55%" $h="24px" $mt="30px" />
          <Block $w="100%" $h="239px" $r="8px" $mt="16px" />
          <Block $w="70%" $h="16px" $mt="14px" />
          <Block $w="40%" $h="16px" $mt="10px" />
        </MainColumn>
      </Columns>
    </Content>
  </BodyContainer>
)

const AmenitySkeleton = () => (
  <IconLine>
    <Block $w="24px" $h="24px" $r="4px" />
    <Block $w="70%" $h="15px" />
  </IconLine>
)

const ListingContent = () => (
  <BodyContainer>
    <Content $pt="40px">
      <Columns>
        <MainColumn>
          <TitleLines widths={['80%', '45%']} height="30px" $mb="20px" />
          {/* location: icon + wrapping link (~2 lines) */}
          <IconLine style={{ alignItems: 'flex-start' }}>
            <Block $w="22px" $h="22px" $r="4px" />
            <div style={{ flex: 1 }}>
              <Block $w="88%" $h="15px" />
              <Block $w="55%" $h="15px" $mt="8px" />
            </div>
          </IconLine>
          {/* guest / feature chips */}
          <InlineRow style={{ marginTop: '20px' }}>
            <Block $w="110px" $h="44px" $r="8px" />
            <Block $w="140px" $h="44px" $r="8px" />
          </InlineRow>
          {/* description + show more */}
          <Block $w="100%" $h="16px" $mt="24px" />
          <Block $w="100%" $h="16px" $mt="10px" />
          <Block $w="72%" $h="16px" $mt="10px" />
          <Block $w="26%" $h="15px" $mt="18px" />
          {/* action buttons: stacked on mobile, side-by-side on tablet+ */}
          <ActionButtons>
            {['bucket', 'gift', 'share'].map((key) => (
              <ActionButton key={key} />
            ))}
          </ActionButtons>
          <Divider />
          {/* amenities — section title is 32px, items are a 2-col icon grid */}
          <Block $w="62%" $h="30px" />
          <AmenitiesGrid>
            {['a', 'b', 'c', 'd', 'e', 'f'].map((key) => (
              <AmenitySkeleton key={key} />
            ))}
          </AmenitiesGrid>
          <Divider />
          {/* details */}
          <Block $w="36%" $h="30px" />
          <Block $w="90%" $h="15px" $mt="20px" />
          <Block $w="82%" $h="15px" $mt="12px" />
          <Block $w="86%" $h="15px" $mt="12px" />
          <Block $w="55%" $h="15px" $mt="12px" />
        </MainColumn>
        <SideColumn>
          <BookingBox>
            <Block $w="45%" $h="22px" />
            <Block $w="100%" $h="40px" $mt="14px" />
            <Block $w="100%" $h="40px" $mt="10px" />
            <Block $w="100%" $h="46px" $r="6px" $mt="14px" />
          </BookingBox>
        </SideColumn>
      </Columns>
    </Content>
  </BodyContainer>
)

const AdventureContent = () => (
  <NarrowerContainer>
    <Content $pt="50px">
      <AuthorRow>
        <Block
          $w="100px"
          $h="100px"
          $r="50%"
          data-testid="detail-skeleton-avatar"
        />
        <div style={{ flex: 1 }}>
          <Block $w="50%" $h="15px" />
          <Block $w="70%" $h="15px" $mt="9px" />
        </div>
      </AuthorRow>
      <TitleLines widths={['82%', '46%']} height="30px" $mt="60px" $mb="18px" />
      <InlineRow>
        <Block $w="40%" $h="16px" />
        <Block $w="24%" $h="16px" />
      </InlineRow>
      <Paragraph lastWidth="55%" />
      <InlineRow style={{ marginTop: '24px' }}>
        <Block $w="154px" $h="57px" $r="6px" />
        <Block $w="110px" $h="57px" $r="6px" />
      </InlineRow>
    </Content>
  </NarrowerContainer>
)

const content = {
  story: StoryContent,
  listing: ListingContent,
  adventure: AdventureContent,
}

const DetailPageSkeleton = ({ variant }: Props) => {
  const Body = content[variant]
  const hasBottomBar = variant === 'story' || variant === 'listing'

  return (
    <Wrapper
      role="status"
      aria-live="polite"
      aria-busy="true"
      $hasBottomBar={hasBottomBar}
    >
      <SrOnly>Loading…</SrOnly>
      <Hero data-testid="detail-skeleton-hero">
        {variant === 'story' && <HeartButton />}
        <GalleryButton />
      </Hero>
      <Body />
      {hasBottomBar && (
        <BottomBar data-testid="detail-skeleton-bottom-bar">
          <BottomBarPrice>
            <Block $w="80px" $h="18px" />
            <Block $w="56px" $h="12px" />
          </BottomBarPrice>
          <Block $w="181px" $h="49px" $r="6px" />
        </BottomBar>
      )}
    </Wrapper>
  )
}

export default DetailPageSkeleton
